import React, { useMemo, useState } from 'react';
import { Button } from '../ui/Button';

const inputClass = 'w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100';

export const MappingSection = ({
  title,
  type,
  targetField,
  catalogue,
  mappings,
  labelFor,
  relevanceOptions,
  busy,
  onAdd,
  onUpdate,
  onDelete
}) => {
  const [targetId, setTargetId] = useState('');
  const [relevanceClass, setRelevanceClass] = useState(relevanceOptions[0].value);
  const [rationale, setRationale] = useState('');
  const [applicabilityCondition, setApplicabilityCondition] = useState('');
  const [sourceReference, setSourceReference] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  const mappedIds = useMemo(
    () => new Set(mappings.map((mapping) => mapping[targetField])),
    [mappings, targetField]
  );
  const available = catalogue.filter((record) => !mappedIds.has(record.id));

  const submitAdd = (event) => {
    event.preventDefault();
    if (!targetId || !rationale.trim()) return;
    onAdd(type, targetId, {
      relevanceClass,
      rationale: rationale.trim(),
      applicabilityCondition: applicabilityCondition.trim(),
      sourceReference: sourceReference.trim()
    });
    setTargetId('');
    setRationale('');
    setApplicabilityCondition('');
    setSourceReference('');
  };

  const startEdit = (mapping) => {
    setEditingId(mapping.id);
    setEditValues({
      relevanceClass: mapping.relevance_class,
      rationale: mapping.rationale,
      applicabilityCondition: mapping.applicability_condition || '',
      sourceReference: mapping.source_reference || ''
    });
  };

  const submitEdit = (event, mappingId) => {
    event.preventDefault();
    if (!editValues.rationale?.trim()) return;
    onUpdate(type, mappingId, {
      ...editValues,
      rationale: editValues.rationale.trim(),
      applicabilityCondition: editValues.applicabilityCondition.trim(),
      sourceReference: editValues.sourceReference.trim()
    });
    setEditingId(null);
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div>
          <h3 className="font-bold text-slate-900">{title}</h3>
          <p className="mt-1 text-xs text-slate-500">{mappings.length} tilknyttet · {available.length} tilgjengelig</p>
        </div>
        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">{mappings.length}</span>
      </div>

      <div className="space-y-3 p-5">
        {mappings.length === 0 && (
          <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">Ingen koblinger ennå.</p>
        )}
        {mappings.map((mapping) => (
          <article key={mapping.id} className="rounded-xl border border-slate-200 p-4">
            {editingId === mapping.id ? (
              <form onSubmit={(event) => submitEdit(event, mapping.id)} className="grid gap-3">
                <label className="grid gap-1 text-xs font-semibold text-slate-600">
                  Relevans
                  <select
                    value={editValues.relevanceClass}
                    onChange={(event) => setEditValues({ ...editValues, relevanceClass: event.target.value })}
                    className={inputClass}
                  >
                    {relevanceOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <label className="grid gap-1 text-xs font-semibold text-slate-600">
                  Begrunnelse
                  <textarea required rows="2" value={editValues.rationale} onChange={(event) => setEditValues({ ...editValues, rationale: event.target.value })} className={inputClass} />
                </label>
                <OptionalText label="Vilkår for anvendelse" value={editValues.applicabilityCondition} onChange={(value) => setEditValues({ ...editValues, applicabilityCondition: value })} />
                <OptionalText label="Kildehenvisning" value={editValues.sourceReference} onChange={(value) => setEditValues({ ...editValues, sourceReference: value })} />
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setEditingId(null)}>Avbryt</Button>
                  <Button type="submit" size="sm" disabled={busy}>Lagre</Button>
                </div>
              </form>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">{labelFor(mapping[targetField]) || 'Ukjent katalogpost'}</p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-blue-700">{relevanceOptions.find((option) => option.value === mapping.relevance_class)?.label || mapping.relevance_class}</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">{mapping.rationale}</p>
                  {mapping.applicability_condition && <p className="mt-2 text-xs text-slate-500">Vilkår: {mapping.applicability_condition}</p>}
                  {mapping.source_reference && <p className="mt-1 text-xs text-slate-500">Kilde: {mapping.source_reference}</p>}
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => startEdit(mapping)}>Rediger</Button>
                  <Button type="button" variant="danger" size="sm" disabled={busy} onClick={() => onDelete(type, mapping.id)}>Fjern</Button>
                </div>
              </div>
            )}
          </article>
        ))}

        <form onSubmit={submitAdd} className="mt-5 grid gap-3 rounded-xl bg-slate-50 p-4">
          <h4 className="text-sm font-bold text-slate-800">Legg til fra katalog</h4>
          <label className="grid gap-1 text-xs font-semibold text-slate-600">
            Tilgjengelig katalogpost
            <select required value={targetId} onChange={(event) => setTargetId(event.target.value)} className={inputClass}>
              <option value="">Velg post</option>
              {available.map((record) => <option key={record.id} value={record.id}>{labelFor(record.id)}</option>)}
            </select>
          </label>
          <label className="grid gap-1 text-xs font-semibold text-slate-600">
            Relevans
            <select value={relevanceClass} onChange={(event) => setRelevanceClass(event.target.value)} className={inputClass}>
              {relevanceOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <label className="grid gap-1 text-xs font-semibold text-slate-600">
            Begrunnelse
            <textarea required rows="2" value={rationale} onChange={(event) => setRationale(event.target.value)} className={inputClass} />
          </label>
          <OptionalText label="Vilkår for anvendelse" value={applicabilityCondition} onChange={setApplicabilityCondition} />
          <OptionalText label="Kildehenvisning" value={sourceReference} onChange={setSourceReference} />
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={busy || available.length === 0}>{busy ? 'Lagrer …' : 'Legg til kobling'}</Button>
          </div>
        </form>
      </div>
    </section>
  );
};

const OptionalText = ({ label, value, onChange }) => (
  <label className="grid gap-1 text-xs font-semibold text-slate-600">
    {label}
    <input value={value} onChange={(event) => onChange(event.target.value)} className={inputClass} />
  </label>
);